import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useCanMutateCloseTemplate } from "@hooks/permissions/organization/useCanMutateCloseTemplate";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { CloseTemplateRow } from "../organizationCloseTemplatesViewConfig";
import { CloseTemplateCallbacks } from "../OrganizationCloseTemplatesList";

type CloseTemplateActionsMeta = ViewTableMeta<CloseTemplateCallbacks, never>;

export const CloseTemplateActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, CloseTemplateRow, string>) => {
        const meta = (table.options.meta ?? {}) as CloseTemplateActionsMeta;
        const closeTemplateId = row.original.id;
        const isArchived = row.original.archivedAt != null;
        const canDelete = row.original.applicationCount === 0 && !isArchived;
        const canMutate = useCanMutateCloseTemplate();
        const canMutateThis = canMutate(row.original.createdByUserId);

        if (
            !meta.callbacks?.onEditCloseTemplate ||
            !meta.callbacks?.onDeleteCloseTemplate ||
            !meta.callbacks?.onArchiveCloseTemplate ||
            !meta.callbacks?.onUnarchiveCloseTemplate ||
            !meta.callbacks?.onApplyCloseTemplate
        ) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    {!isArchived && (
                        <Button
                            variant="primary"
                            size="sm"
                            className={viewTableStyles.showOnHover}
                            onClick={() =>
                                meta.callbacks?.onApplyCloseTemplate(
                                    closeTemplateId,
                                )
                            }
                        >
                            Apply
                        </Button>
                    )}
                    <Menu.More className={viewTableStyles.showOnHover}>
                        {!isArchived && canMutateThis && (
                            <Menu.Item
                                onClick={() =>
                                    meta.callbacks?.onEditCloseTemplate(
                                        closeTemplateId,
                                    )
                                }
                            >
                                Edit details
                            </Menu.Item>
                        )}
                        {!isArchived && (
                            <Menu.Item
                                onClick={() =>
                                    meta.callbacks?.onDuplicateCloseTemplate(
                                        closeTemplateId,
                                    )
                                }
                            >
                                Duplicate
                            </Menu.Item>
                        )}
                        {isArchived && canMutateThis ? (
                            <Menu.Item
                                onClick={() =>
                                    meta.callbacks?.onUnarchiveCloseTemplate(
                                        closeTemplateId,
                                    )
                                }
                            >
                                Activate
                            </Menu.Item>
                        ) : (
                            !isArchived &&
                            canMutateThis && (
                                <Menu.Item
                                    destructive
                                    onClick={() =>
                                        meta.callbacks?.onArchiveCloseTemplate(
                                            closeTemplateId,
                                        )
                                    }
                                >
                                    Archive
                                </Menu.Item>
                            )
                        )}
                        {canDelete && canMutateThis && (
                            <Menu.Item
                                destructive
                                onClick={() =>
                                    meta.callbacks?.onDeleteCloseTemplate(
                                        closeTemplateId,
                                    )
                                }
                            >
                                Delete
                            </Menu.Item>
                        )}
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);

CloseTemplateActionsCell.displayName = "CloseTemplateActionsCell";
